// Outlook e-mails on the client timeline (Oct 2026, idea 35). The platform finds e-mails between the advisor's Outlook
// and a CRM client (by the client's e-mail addresses) and hands over subject, date, direction and an Outlook link — the
// text only for clients the advisor marked "keep the e-mail text". Each one becomes a communication (channel e-mail)
// with a timeline entry, once: the id is derived from the platform's key, so the same e-mail is never recorded twice
// (on any device). Nothing is sent; nothing existing is changed or deleted. No schema change (communications.data).
import { stableId } from './ids.ts';
import { DomainError, clean, created, put, requirePerson, timeline, touched, type Ctx } from './cmd.ts';
import type { Changeset, Put, WorkspaceView } from './changeset.ts';

export interface OutlookMailInput { key: string; personId: string; direction: 'in' | 'out'; at: string; subject?: string | null; link?: string | null; body?: string | null }
export const outlookMailId = (key: string) => stableId('outlook-mail', key);
/** whether the advisor keeps the text of this client's e-mails (off by default) */
export const keepsOutlookText = (v: WorkspaceView, personId: string) => (v.get('persons', personId)?.data as { outlookMailBodies?: boolean } | null | undefined)?.outlookMailBodies === true;

/** records the e-mails that are not in the CRM yet; returns null when there is nothing new */
export function recordOutlookMails(v: WorkspaceView, items: readonly OutlookMailInput[], ctx: Ctx): Changeset | null {
  const puts: Put[] = [];
  let n = 0;
  for (const m of items) {
    if (!/^[0-9a-f]{40}$/.test(String(m.key)) || (m.direction !== 'in' && m.direction !== 'out') || Number.isNaN(Date.parse(String(m.at)))) continue;
    const p = v.get('persons', m.personId);
    if (!p) continue; // the client was deleted meanwhile
    const id = outlookMailId(m.key);
    if (v.get('communications', id)) continue;
    const subject = clean(m.subject).slice(0, 300) || null;
    const link = /^https:\/\/[^\s]+$/.test(String(m.link ?? '')) ? String(m.link).slice(0, 1000) : null;
    // the text only when the advisor keeps it for this client (checked again here, not only on the platform)
    const body = keepsOutlookText(v, p.id) ? clean(m.body).slice(0, 4000) || null : null;
    const at = new Date(m.at).toISOString();
    const row = created<'communications'>({ ...ctx }, { id, personId: p.id, prospectId: null, at, channel: 'email', direction: m.direction, subject, summary: body, outcome: null, taskId: null, data: { source: 'outlook', key: m.key, link } });
    puts.push(put('communications', row));
    const ev = timeline({ ...ctx, now: at }, p.id, 'email', subject ?? (m.direction === 'in' ? 'outlook.mail.in_untitled' : 'outlook.mail.out_untitled'), body, { table: 'communications', id });
    puts.push({ ...ev, row: { ...(ev.row as object), id: stableId('outlook-mail-timeline', m.key), data: { source: 'outlook', direction: m.direction, link } } } as Put);
    n++;
  }
  if (!n) return null;
  return { action: 'outlook.mail.record', entity: 'communications', summary: String(n), puts, deletes: [] };
}

/** the advisor decides per client whether the text of e-mails is kept (only e-mails found after the change) */
export function setOutlookMailBodies(v: WorkspaceView, personId: string, keep: boolean, ctx: Ctx): Changeset {
  const p = requirePerson(v, personId);
  if (typeof keep !== 'boolean') throw new DomainError('outlook.mail.keep');
  const data = { ...((p.data ?? {}) as Record<string, unknown>), outlookMailBodies: keep };
  return { action: 'outlook.mail.bodies', entity: 'persons', entityId: p.id, summary: keep ? 'on' : 'off', puts: [put('persons', touched(ctx, p, { data }))], deletes: [] };
}
